import moment from "moment";
import { DatesRange } from "../../Models/Dates/DatesRange";
import { PriceFactorMode, Product } from "../../Models/Product/Product";
import { RentPriceCalculator } from "../RentPriceCalculator";
import { QuantityDiscountCalculator } from "../QuantityDiscountCalculator";
import { Basket } from "./Basket";
import { BasketItem } from "./BasketItem";
import { Ref, RefNumber } from "@tblabs/truffle";
import { BasketItemType } from "../../Models/Basket/BasketItemType";
import { RentBasketStorageItem } from "./Storage/BasketStorageItem";
import { DoDateRangesOverlap } from "../../Utils/DateRangeOverlap";


export class RentBasketItem extends BasketItem
{
    public FinalDeposit = new RefNumber();

    constructor(
        private basket: Basket,
        public Product: Product | undefined,
        public ProductOptionId: Ref<string>,
        public Quantity: Ref<number>,
        public Dates: DatesRange)
    {
        super(BasketItemType.Rent, Product, ProductOptionId, Quantity, basket.Discounter);

        this.Dates.OnChange((start, end, days) => this.UpdateOnInputChange());

        this.UpdateOnInputChange();
    }

    public UpdateOnInputChange(): void
    {
        if (!this.Product)
            return;

        const rentStart = this.Dates.Start.value;
        const quantity = this.Quantity.value;
        const rentDays = this.Dates.DaysCount.value;
        const rentPrices = this.Product.RentPrices.Items.map(x => ({ price: x.Value.value, days: x.Days.value }));
        const quantityDiscounts = this.Product.Discounts.Items.map(x => ({ quantity: x.Quantity.value, discountPercent: x.Percent.value }));
        const optionFactor = this.Product.Options.GetPriceFactor(this.ProductOptionId.value);
        const generalFactor = this.basket.Config.GeneralPriceFactor.value;

        const rentPrice = RentPriceCalculator.Calc(rentPrices, rentDays);
        const { discountMultiplier, discountPercent } = QuantityDiscountCalculator.Calc(quantityDiscounts, quantity);

        const datesFactor = this.Product.PriceFactor.Items.find(x => DoDateRangesOverlap(x.Start.value, x.End.value, this.Dates.Start.value, this.Dates.End.value));
        const mode = datesFactor?.Mode?.value;
        if (mode == PriceFactorMode.Multiply)
        {
            const extraFactor = datesFactor?.Value?.value ?? 1;
            this.FinalPriceBeforeDiscount.value = +(quantity * rentPrice * optionFactor * generalFactor * extraFactor).toFixed(0);
        }
        else if (mode == PriceFactorMode.Constant)
        {
            const extraValue = datesFactor?.Value?.value ?? 0;
            this.FinalPriceBeforeDiscount.value = +(quantity * rentPrice * optionFactor * generalFactor + (extraValue * quantity)).toFixed(0);
        }
        else
        {
            this.FinalPriceBeforeDiscount.value = +(quantity * rentPrice * optionFactor * generalFactor).toFixed(0);
        }
        const info = datesFactor?.Info?.value ?? "";
        this.ExtraInfo.value = info
            .replace("{value}", datesFactor?.Value?.value?.toString() ?? "")
            .replace("{start}", moment(datesFactor?.Start?.value).format("DD.MM.YYYY"))
            .replace("{end}", moment(datesFactor?.End?.value).format("DD.MM.YYYY"));
        // const otherDiscountsPercent = this.Discounter.GetTotalPercent();
        // const otherDiscountsMultiplier = 1 - otherDiscountsPercent / 100;
        const otherDiscountsValue = this.Discounter.GetTotalValue(this.FinalPriceBeforeDiscount.value);
        // this.FinalPriceAfterDiscount.value = +(this.FinalPriceBeforeDiscount.value * discountMultiplier * otherDiscountsMultiplier).toFixed(0);
        this.FinalPriceAfterDiscount.value = +(this.FinalPriceBeforeDiscount.value * discountMultiplier - otherDiscountsValue).toFixed(0);
        this.Label.value = `Wypożyczenie na ${rentDays} ${rentDays === 1 ? "dzień" : "dni"} od ${moment(rentStart).format("DD.MM.YYYY")}`;
        this.FinalDeposit.value = quantity * this.Product.Deposit.value;
        this.QuantityDiscountPercent.value = discountPercent;

        this.onChangeHandlers.Call();
    }

    public AsRaw(): RentBasketStorageItem | undefined
    {
        if (!this.Product)
            return undefined;

        return ({
            Type: BasketItemType.Rent,
            ProductId: this.Product.Id,
            ProductOptionId: this.ProductOptionId.value,
            Quantity: this.Quantity.value,
            RentTime: { Start: this.Dates.Start.value, End: this.Dates.End.value },
        });
    }
}
